/**
 * Admin Dashboard Context
 * Manages admin authentication state and provides auth methods
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AdminContext = createContext();

// Use environment variable or default based on environment
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    // Use the Heroku backend URL in production
    return 'https://yemberzal-app-28481d1d0b39.herokuapp.com';
  }
  return 'http://localhost:8000';
};

const API_URL = getApiUrl();

export function AdminProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Clear session function
  const clearSession = () => {
    localStorage.removeItem('admin_dashboard_admin');
    localStorage.removeItem('admin_dashboard_token');
    localStorage.removeItem('admin_dashboard_refresh_token');
    localStorage.removeItem('admin_dashboard_token_expiry');
    setAdmin(null);
    setAccessToken(null);
  };

  // Refresh access token function
  const refreshAccessToken = useCallback(async (refreshToken) => {
    try {
      const response = await fetch(`${API_URL}/api/admin-dashboard/refresh-token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        const newAccessToken = data.access_token;
        const newExpiry = new Date().getTime() + (60 * 60 * 1000); // 1 hour

        localStorage.setItem('admin_dashboard_token', newAccessToken);
        localStorage.setItem('admin_dashboard_token_expiry', newExpiry.toString());
        setAccessToken(newAccessToken);
        return true;
      } else {
        clearSession();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearSession();
      return false;
    }
  }, [API_URL]);

  // Check for existing session on mount
  useEffect(() => {
    const storedAdmin = localStorage.getItem('admin_dashboard_admin');
    const storedToken = localStorage.getItem('admin_dashboard_token');
    const tokenExpiry = localStorage.getItem('admin_dashboard_token_expiry');

    if (storedAdmin && storedToken && tokenExpiry) {
      // Check if token is still valid
      if (new Date().getTime() < parseInt(tokenExpiry)) {
        setAdmin(JSON.parse(storedAdmin));
        setAccessToken(storedToken);
      } else {
        // Token expired, try to refresh
        const refreshToken = localStorage.getItem('admin_dashboard_refresh_token');
        if (refreshToken) {
          refreshAccessToken(refreshToken);
        } else {
          clearSession();
        }
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const loginUrl = `${API_URL}/api/admin-dashboard/login/`;
    
    try {
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        return { 
          success: false, 
          error: `Server error (${response.status}). Please check if the API server is running.` 
        };
      }

      const data = await response.json();

      if (response.ok && data.success) {
        // Store session
        setAdmin(data.admin);
        setAccessToken(data.access_token);
        
        localStorage.setItem('admin_dashboard_admin', JSON.stringify(data.admin));
        localStorage.setItem('admin_dashboard_token', data.access_token);
        localStorage.setItem('admin_dashboard_refresh_token', data.refresh_token);
        localStorage.setItem('admin_dashboard_token_expiry', (data.expires_at * 1000).toString());

        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        return { 
          success: false, 
          error: `Cannot connect to server. Please ensure the backend server is running.` 
        };
      }
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = () => {
    clearSession();
  };

  // Helper function to make authenticated API calls
  const authFetch = async (endpoint, options = {}) => {
    const headers = {
      ...options.headers,
    };

    // Only set Content-Type if not uploading FormData
    // (browser will set multipart/form-data automatically for FormData)
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const fullUrl = `${API_URL}${endpoint}`;
    console.log('=== AUTH FETCH ===');
    console.log('URL:', fullUrl);
    console.log('Method:', options.method || 'GET');
    console.log('Headers:', headers);
    console.log('Body type:', options.body?.constructor?.name);
    console.log('Access Token exists:', !!accessToken);

    const response = await fetch(fullUrl, {
      ...options,
      headers,
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    // If unauthorized, try to refresh token
    if (response.status === 401) {
      console.log('Got 401, attempting token refresh...');
      const refreshToken = localStorage.getItem('admin_dashboard_refresh_token');
      if (refreshToken) {
        const refreshed = await refreshAccessToken(refreshToken);
        if (refreshed) {
          console.log('Token refreshed, retrying request...');
          // Retry the request with new token
          headers['Authorization'] = `Bearer ${localStorage.getItem('admin_dashboard_token')}`;
          return fetch(fullUrl, { ...options, headers });
        }
      }
    }

    return response;
  };

  const value = {
    admin,
    accessToken,
    loading,
    isAuthenticated: !!admin && !!accessToken,
    login,
    logout,
    authFetch,
    refreshAccessToken,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}

export default AdminContext;
